import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useResponsive, useRoleNavigation, useAutoTranslate, useUnsavedChangesWarning } from '../../hooks';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { extractKpiErrorMessage, validateKpiRequiredFields } from '../../utils/kpiErrors';
import { normalizeArabicNumbers } from '../../utils/rtl';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Breadcrumb, KPIDeleteConfirmModal, MultiSelectDropdown } from '../../components';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { goalsApi, kpisApi, activitiesApi, initiativesApi, organizationalUnitsApi } from '../../services/apiService';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { StrategicGoalList, OrganizationalUnitList, KPIType, Frequency, Polarity } from '../../types';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import KpiAnnualTargetSection, {
  KpiTargetConfig,
  emptyKpiTargetConfig,
  buildKpiTargetPayload,
  configFromKpi,
} from '../kpis/components/KpiAnnualTargetSection';
type RouteProps = RouteProp<MainStackParamList, 'AddActivity'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// KPI Form Interface
// ============================================================

interface KPIFormData {
  id: string;
  name: string;
  weight: string;
  description: string;
  kpiType: KPIType | '';
  polarity: Polarity | '';
  startDate: string;
  unit: string;
  frequency: Frequency | '';
  formula: string;
  targetValue: string;
  worstAcceptableValue: string;
  dataSource: string;
  target: KpiTargetConfig;
  isExpanded: boolean;
  isExisting?: boolean; // Flag for existing KPIs loaded from API
  responsibleUnits: string[];
}

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MenuIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 6H20M4 12H20M4 18H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown = ({ value, options, placeholder, onChange, disabled }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<View>(null);
  const menuRef = useRef<View>(null);
  const { colors, isDark } = useTheme();
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    icon: colors.textMuted,
    disabledBg: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBorder: isDark ? colors.border : tokens.colors.raw.slate["100"],
  };

  const handleOpenDropdown = () => {
    if (disabled) return;

    if (!isOpen && buttonRef.current) {
      // @ts-ignore - measureInWindow is available on View
      buttonRef.current.measureInWindow((x: number, y: number, width: number, height: number) => {
        setDropdownPosition({
          top: y + height + 4,
          left: x,
          width,
        });
      });
    }

    setIsOpen(!isOpen);
  };

  // Close on outside click and on scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      // Don't close if clicking inside the dropdown menu or scrollbar
      const menuEl = menuRef.current as any;
      if (menuEl && menuEl.contains && menuEl.contains(e.target)) return;
      setIsOpen(false);
    };

    const handleScroll = (e: Event) => {
      // Don't close if scrolling inside the dropdown menu
      const menuEl = menuRef.current as any;
      if (menuEl && menuEl.contains && menuEl.contains(e.target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const dropdownMenu = isOpen
    ? ReactDOM.createPortal(
        <View
          ref={menuRef}
          // @ts-ignore - web style
          onMouseDown={(e: any) => e.stopPropagation()}
          style={[
            styles.dropdownMenu,
            {
              position: 'fixed' as any,
              top: dropdownPosition.top,
              start: dropdownPosition.left,
              width: dropdownPosition.width,
              maxHeight: 200,
              overflow: 'auto' as any,
              backgroundColor: dropdownTheme.menuBg,
              borderColor: dropdownTheme.menuBorder,
            },
          ]}
        >
          {options.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[styles.dropdownItem, { borderBottomColor: dropdownTheme.itemBorder }]}
              onPress={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <Text style={[styles.dropdownItemText, { color: dropdownTheme.text }]}>{option.label}</Text>
            </TouchableOpacity>
          ))}
        </View>,
        document.body,
      )
    : null;

  return (
    <>
      <View style={styles.dropdownContainer} ref={buttonRef}>
        <TouchableOpacity
          style={[
            styles.dropdownButton,
            { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
            disabled && { backgroundColor: dropdownTheme.disabledBg },
          ]}
          onPress={handleOpenDropdown}
          disabled={disabled}
        >
          <ChevronDownIcon size={16} color={dropdownTheme.icon} />
          <Text
            style={[
              styles.dropdownButtonText,
              { color: selectedOption ? dropdownTheme.text : dropdownTheme.placeholder },
            ]}
          >
            {selectedOption?.label || placeholder}
          </Text>
        </TouchableOpacity>
      </View>
      {dropdownMenu}
    </>
  );
};

// ============================================================
// KPI Options
// ============================================================

// Note: These will be translated at render time using the t function
const getKpiTypeOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: 'strategic', label: t('kpis.strategic') },
  { value: 'operational', label: t('kpis.operational') },
  { value: 'efficiency', label: t('kpis.efficiency') },
  { value: 'effectiveness', label: t('kpis.effectiveness') },
  { value: 'impact', label: t('kpis.impact') },
];

const getPolarityOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: 'positive', label: t('kpis.ascending') },
  { value: 'negative', label: t('kpis.descending') },
];

const getUnitOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: '%', label: t('kpis.percentage') },
  { value: 'number', label: t('kpis.number') },
  { value: 'currency', label: t('kpis.currency') },
];

const getFrequencyOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: 'daily', label: t('kpis.daily') },
  { value: 'weekly', label: t('kpis.weekly') },
  { value: 'monthly', label: t('kpis.monthly') },
  { value: 'quarterly', label: t('kpis.quarterly') },
  { value: 'half_yearly', label: t('kpis.semiAnnual') },
  { value: 'yearly', label: t('kpis.annual') },
];

// ============================================================
// Main Component
// ============================================================

export default function AddActivityScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { initiativeId, activityId, scrollToKpi } = route.params || {};
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate } = useAutoTranslate();

  useEffect(() => {
    if (isViewOnly) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigateToHome();
    }
  }, [isViewOnly, navigation, navigateToHome]);

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"],
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiHeader: colors.card,
    kpiHeaderTitle: colors.textPrimary,
    kpiNumberBadge: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["200"],
    kpiSubsection: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    weightInputWrapper: colors.card,
    weightButton: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
    weightButtonBorder: isDark ? '#4B5563' : tokens.colors.raw.slate["300"],
    addKpiButton: colors.card,
    addKpiButtonBorder: isDark ? '#4B5563' : tokens.colors.raw.slate["300"],
    addKpiButtonText: colors.textPrimary,
    loadingText: colors.textMuted,
    dateInput: colors.card,
    dateInputBorder: colors.border,
  };

  // Refs for scrolling to KPI section
  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionY = useRef<number>(0);

  // Edit mode flag
  const isEditMode = !!activityId;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    strategicGoal: '',
    weight: '',
    responsibleUnits: [] as string[],
    budget: '',
    resources: '',
    partners: '',
    technicalResources: '',
    startDate: '',
    endDate: '',
  });

  // KPIs state
  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [deletedKpiIds, setDeletedKpiIds] = useState<number[]>([]);
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ id: string; backendId: number | null; name: string } | null>(null);

  useUnsavedChangesWarning(!!formData.name || !!formData.description);

  const [loading, setLoading] = useState(false);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [organizationalUnits, setOrganizationalUnits] = useState<OrganizationalUnitList[]>([]);

  // Fetch goals and organizational units for dropdowns
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [goalsResponse, unitsResponse] = await Promise.all([
          goalsApi.list({ page_size: 100 }),
          organizationalUnitsApi.list({ page_size: 100 }),
        ]);
        setGoals(goalsResponse.results || []);
        setOrganizationalUnits(unitsResponse.results || []);
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      } finally {
        setGoalsLoading(false);
      }
    };
    fetchDropdownData();
  }, []);

  // Fetch activity data when in edit mode
  useEffect(() => {
    const fetchActivity = async () => {
      if (!isEditMode || !activityId) return;

      try {
        const activityData = await activitiesApi.get(activityId);

        // Fetch initiative to get the linked strategic goal
        let strategicGoalId = '';
        const initId = activityData.initiative || initiativeId;
        if (initId) {
          try {
            const initiativeData = await initiativesApi.get(initId);
            strategicGoalId = initiativeData.strategic_goal ? initiativeData.strategic_goal.toString() : '';
          } catch (e) {
            console.error('Error fetching initiative for strategic goal:', e);
          }
        }

        setFormData({
          name: activityData.name || '',
          description: activityData.description || '',
          strategicGoal: strategicGoalId,
          weight: activityData.weight != null ? String(activityData.weight) : '',
          responsibleUnits: (activityData.responsible_units || []).map((id: number) => id.toString()),
          budget: activityData.budget || '',
          resources: activityData.human_resources || '',
          partners: activityData.partners || '',
          technicalResources: activityData.technical_resources || '',
          startDate: activityData.start_date || '',
          endDate: activityData.end_date || '',
        });

        // Fetch existing KPIs for this activity
        try {
          const kpisResponse = await kpisApi.list({ source_activity: activityId, page_size: 100 });
          if (kpisResponse.results && kpisResponse.results.length > 0) {
            const existingKpis: KPIFormData[] = kpisResponse.results.map((kpi: any) => ({
              id: kpi.id.toString(),
              name: kpi.name || '',
              weight: kpi.weight || '30',
              description: kpi.description || '',
              kpiType: kpi.kpi_type || '',
              polarity: kpi.polarity || '',
              startDate: kpi.measurement_start_date || '',
              unit: kpi.unit || '',
              frequency: kpi.frequency || '',
              formula: kpi.measurement_formula || '',
              targetValue: kpi.target_value || '0.0',
              worstAcceptableValue: kpi.worst_acceptable_value != null ? String(kpi.worst_acceptable_value) : '',
              dataSource: kpi.data_source || '',
              target: configFromKpi(kpi),
              isExpanded: false,
              isExisting: true, // Mark as existing KPI
              responsibleUnits: (kpi.responsible_units || []).map((id: number) => String(id)),
            }));
            setKpis(existingKpis);
          }
        } catch (kpiError) {
          console.error('Error fetching activity KPIs:', kpiError);
        }
      } catch (error) {
        console.error('Error fetching activity:', error);
        showError(t('messages.error.loadFailed'));
      } finally {
        setInitialLoading(false);
      }
    };
    fetchActivity();
  }, [isEditMode, activityId, showError]);

  // Scroll to KPI section function
  const scrollToKpiSection = useCallback(() => {
    if (scrollToKpi && kpiSectionY.current > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: kpiSectionY.current - 100, animated: true });
      }, 100);
    }
  }, [scrollToKpi]);

  // Scroll to KPI section when data is loaded
  useEffect(() => {
    if (!initialLoading && scrollToKpi) {
      scrollToKpiSection();
    }
  }, [initialLoading, scrollToKpi, scrollToKpiSection]);

  const goalOptions: DropdownOption[] = goals.map(goal => ({
    value: goal.id.toString(),
    label: translate(goal.title),
  }));

  const orgUnitOptions: DropdownOption[] = organizationalUnits.map(unit => ({
    value: unit.id.toString(),
    label: translate(unit.name),
  }));

  // Create empty KPI form
  const createEmptyKPI = (): KPIFormData => ({
    id: Date.now().toString(),
    name: '',
    weight: '30',
    description: '',
    kpiType: '',
    polarity: '',
    startDate: '',
    unit: '%',
    frequency: 'yearly',
    formula: '',
    targetValue: '0.0',
    worstAcceptableValue: '',
    dataSource: '',
    target: emptyKpiTargetConfig,
    isExpanded: true,
    responsibleUnits: [],
  });

  // Add new KPI
  const handleAddKPI = () => {
    setKpis([...kpis, createEmptyKPI()]);
  };

  // Remove KPI — open confirmation first
  const handleRemoveKPI = (kpiId: string) => {
    const kpiToRemove = kpis.find(kpi => kpi.id === kpiId);
    const numericId = parseInt(kpiId, 10);
    const backendId = kpiToRemove?.isExisting && !isNaN(numericId) ? numericId : null;
    setKpiPendingDelete({
      id: kpiId,
      backendId,
      name: kpiToRemove?.name || '',
    });
  };

  const confirmRemoveKPI = () => {
    if (!kpiPendingDelete) return;
    const { id, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      setDeletedKpiIds(prev => [...prev, backendId]);
    }
    setKpis(prev => prev.filter(kpi => kpi.id !== id));
    setKpiPendingDelete(null);
  };

  // Toggle KPI expand/collapse
  const handleToggleKPI = (kpiId: string) => {
    setKpis(kpis.map(kpi =>
      kpi.id === kpiId ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi
    ));
  };

  // Update KPI field
  const updateKPIField = (kpiId: string, field: string, value: string) => {
    setKpis(kpis.map(kpi =>
      kpi.id === kpiId ? { ...kpi, [field]: value } : kpi
    ));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.required', { field: t('initiatives.activityName') });
    }
    if (!formData.description.trim()) {
      newErrors.description = t('validation.required', { field: t('initiatives.activityDescription') });
    }
    if (formData.responsibleUnits.length === 0) {
      newErrors.responsibleUnits = t('validation.organizationalUnitRequired');
    }
    if (!formData.startDate.trim()) {
      newErrors.startDate = t('validation.required', { field: t('initiatives.startDate') });
    }
    if (!formData.endDate.trim()) {
      newErrors.endDate = t('validation.required', { field: t('initiatives.endDate') });
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    const kpiError = validateKpiRequiredFields(kpis, t);
    if (kpiError) { showError(kpiError); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequired'));
      return;
    }

    setLoading(true);
    try {
      // Prepare activity data
      const activityData: Record<string, any> = {
        initiative: initiativeId,
        name: formData.name,
        description: formData.description,
        start_date: formData.startDate || null,
        end_date: formData.endDate || null,
        human_resources: formData.resources || '',
        technical_resources: formData.technicalResources || '',
        partners: formData.partners || '',
      };

      // Add budget if provided
      if (formData.budget.trim()) {
        const budgetValue = parseFloat(formData.budget);
        if (!isNaN(budgetValue) && budgetValue >= 0) {
          activityData.budget = budgetValue.toFixed(2);
        }
      }

      // Add weight if provided (decimal, max 5 digits / 2 decimals)
      if (formData.weight.trim()) {
        const weightValue = parseFloat(formData.weight);
        if (!isNaN(weightValue) && weightValue >= 0) {
          activityData.weight = weightValue.toFixed(2);
        }
      }

      // Add responsible_units
      if (formData.responsibleUnits.length > 0) {
        activityData.responsible_units = formData.responsibleUnits.map((id: string) => parseInt(id));
      }

      console.log('Creating activity with data:', activityData);

      let createdActivity;
      if (isEditMode && activityId) {
        // Update existing activity
        createdActivity = await activitiesApi.update(activityId, activityData);
        console.log('Activity updated:', createdActivity);
      } else {
        // Create new activity
        createdActivity = await activitiesApi.create(activityData);
        console.log('Activity created:', createdActivity);
      }

      // Delete removed KPIs from backend
      let kpiDeleteFailed = 0;
      if (deletedKpiIds.length > 0) {
        for (const kpiId of deletedKpiIds) {
          try {
            await kpisApi.delete(kpiId);
          } catch (deleteError: any) {
            kpiDeleteFailed++;
          }
        }
      }
      if (kpiDeleteFailed > 0) {
        showError(t('kpis.deleteFailed'));
      }

      // Handle KPIs - create new ones and update existing ones
      const validKpis = kpis.filter(kpi => kpi.name.trim());
      const savedActivityId = createdActivity?.id || activityId;
      const kpiErrors: string[] = [];
      if (validKpis.length > 0 && savedActivityId) {
        for (const kpi of validKpis) {
          const kpiData = {
            name: kpi.name,
            description: kpi.description,
            source_activity: savedActivityId,
            kpi_type: (kpi.kpiType || 'operational') as KPIType,
            polarity: (kpi.polarity || 'positive') as Polarity,
            unit: kpi.unit || '%',
            frequency: (kpi.frequency || 'monthly') as Frequency,
            target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
            worst_acceptable_value:
              kpi.polarity === 'negative' && kpi.unit !== '%' && kpi.worstAcceptableValue !== ''
                ? (parseFloat(kpi.worstAcceptableValue) || 0).toFixed(2)
                : null,
            weight: kpi.weight || '0',
            measurement_start_date: kpi.startDate || null,
            measurement_formula: kpi.formula || '',
            data_source: kpi.dataSource || '',
            source_type: 'activity',
            responsible_units: kpi.responsibleUnits.map(Number),
          };

          // Check if this is an existing KPI or new one
          if (kpi.isExisting) {
            const numericId = parseInt(kpi.id, 10);
            if (!isNaN(numericId)) {
              try {
                await kpisApi.update(numericId, {
                  ...kpiData,
                  ...buildKpiTargetPayload(kpi.target, {
                    unit: kpi.unit,
                    frequency: kpi.frequency,
                    startDate: kpi.startDate,
                  }),
                });
              } catch (updateError: any) {
                const errMsg = extractKpiErrorMessage(updateError?.response?.data);
                kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
                console.warn(`KPI ${kpi.id} update failed:`, updateError?.response?.data);
              }
              continue;
            }
          }
          // Create new KPI
          try {
            await kpisApi.create({
              ...kpiData,
              ...buildKpiTargetPayload(kpi.target, {
                unit: kpi.unit,
                frequency: kpi.frequency,
                startDate: kpi.startDate,
              }),
            });
          } catch (createError: any) {
            const errMsg = extractKpiErrorMessage(createError?.response?.data);
            kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
            console.warn(`KPI create failed:`, createError?.response?.data);
          }
        }
      }

      if (kpiErrors.length > 0) {
        showError(kpiErrors.join('\n'));
      }

      showSuccess(isEditMode ? t('initiatives.activityUpdateSuccess') : t('initiatives.activityCreateSuccess'));
      queryClient.invalidateQueries({ queryKey: queryKeys.initiatives.all });
      // Go to the saved activity's detail page instead of goBack() (which can
      // land on the dashboard when the stack is shallow).
      if (savedActivityId) {
        navigation.navigate('ActivityDetail', { id: savedActivityId });
      } else {
        navigation.goBack();
      }
    } catch (error: any) {
      console.error('Error saving activity:', error);
      console.error('Error response:', error.response?.data);

      // Build detailed error message
      let errorMessage = t('messages.error.saveFailed');
      const errorData = error.response?.data;

      if (errorData) {
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.non_field_errors?.[0]) {
          errorMessage = errorData.non_field_errors[0];
        } else {
          const fieldErrors: string[] = [];
          const fieldNames: Record<string, string> = {
            name: t('initiatives.activityName'),
            description: t('common.description'),
            initiative: t('navigation.initiatives'),
            responsible_units: t('organizations.responsibleUnit'),
            start_date: t('initiatives.startDate'),
            end_date: t('initiatives.endDate'),
            budget: t('initiatives.budget'),
            human_resources: t('initiatives.resources'),
          };

          for (const [field, messages] of Object.entries(errorData)) {
            if (Array.isArray(messages) && messages.length > 0) {
              const fieldName = fieldNames[field] || field;
              fieldErrors.push(`${fieldName}: ${messages[0]}`);
            }
          }

          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join(' | ');
          }
        }
      }

      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: { formData, kpis },
    ready: !initialLoading,
    onExit: handleCancel,
  });

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Render KPI Form Card
  const renderKPICard = (kpi: KPIFormData, index: number) => (
    <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }]}>
      {/* KPI Header */}
      <View style={[styles.kpiCardHeader, { backgroundColor: themeColors.kpiHeader, borderBottomColor: themeColors.kpiCardBorder }]}>
        <View style={styles.kpiCardHeaderLeft}>
          <TouchableOpacity
            style={[styles.kpiHeaderButton, { backgroundColor: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"], borderColor: themeColors.kpiCardBorder }]}
            onPress={() => handleToggleKPI(kpi.id)}
          >
            {kpi.isExpanded ? (
              <ChevronUpIcon size={16} color={themeColors.iconColor} />
            ) : (
              <ChevronDownIcon size={16} color={themeColors.iconColor} />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.kpiHeaderButton, { backgroundColor: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"], borderColor: themeColors.kpiCardBorder }]}
            onPress={() => handleRemoveKPI(kpi.id)}
          >
            <TrashIcon size={16} color={themeColors.iconColor} />
          </TouchableOpacity>
        </View>
        <View style={styles.kpiCardHeaderRight}>
          <Text style={[styles.kpiCardTitle, { color: themeColors.kpiHeaderTitle }]}>
            {kpi.name || `${t('kpis.kpiName')} ${index + 1}`}
          </Text>
          <View style={[styles.kpiNumberBadge, { backgroundColor: themeColors.kpiNumberBadge }]}>
            <Text style={[styles.kpiNumberText, { color: themeColors.kpiHeaderTitle }]}>{index + 1}</Text>
          </View>
          <MenuIcon size={16} color={themeColors.iconColor} />
        </View>
      </View>

      {/* KPI Content (Collapsible) */}
      {kpi.isExpanded && (
        <View style={styles.kpiCardContent}>
          {/* Basic Information */}
          <View style={[styles.kpiSection, { backgroundColor: themeColors.kpiSubsection }]}>
            <Text style={[styles.kpiSectionTitle, { color: themeColors.kpiHeaderTitle }]}>{t('goals.basicInfo')}</Text>

            <View style={styles.row}>
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <Button variant="secondary" size="sm" onPress={() => {}}>
                    {t('goals.setWeight')}
                  </Button>
                  <Text style={styles.percentSymbol}>%</Text>
                  <TextInput
                    style={styles.weightInput}
                    value={kpi.weight}
                    onChangeText={(v) => updateKPIField(kpi.id, 'weight', normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
              </View>

              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.kpiName')}</Text>
                </View>
                <TextInput
                  style={styles.input}
                  value={kpi.name}
                  onChangeText={(v) => updateKPIField(kpi.id, 'name', v)}
                  placeholder=""
                  textAlign="right"
                />
              </View>
            </View>

            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={styles.label}>{t('kpis.kpiDescription')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={kpi.description}
                onChangeText={(v) => updateKPIField(kpi.id, 'description', v)}
                placeholder=""
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>
          </View>

          {/* Classification */}
          <View style={[styles.kpiSection, { zIndex: 30, backgroundColor: themeColors.kpiSubsection }]}>
            <Text style={[styles.kpiSectionTitle, { color: themeColors.kpiHeaderTitle }]}>{t('kpis.classification')}</Text>

            <View style={[styles.row, { zIndex: 20 }]}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.polarity')}</Text>
                </View>
                <Dropdown
                  value={kpi.polarity}
                  options={getPolarityOptions(t)}
                  placeholder={t('kpis.selectPolarityHint')}
                  onChange={(v) => updateKPIField(kpi.id, 'polarity', v)}
                />
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.kpiClassification')}</Text>
                </View>
                <Dropdown
                  value={kpi.kpiType}
                  options={getKpiTypeOptions(t)}
                  placeholder={t('kpis.selectClassification')}
                  onChange={(v) => updateKPIField(kpi.id, 'kpiType', v)}
                />
              </View>
            </View>

            <View style={{ marginTop: tokens.spacing.absolute['3'], zIndex: 5 }}>
              <Text style={[styles.label, { marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
              <MultiSelectDropdown
                values={kpi.responsibleUnits}
                options={(() => {
                  const related = formData.responsibleUnits;
                  const opts = orgUnitOptions.filter(o => related.includes(o.value));
                  kpi.responsibleUnits.forEach(v => {
                    if (!opts.find(o => o.value === v)) {
                      const found = orgUnitOptions.find(o => o.value === v);
                      if (found) opts.push(found);
                    }
                  });
                  return opts;
                })()}
                placeholder={t('initiatives.selectResponsibleUnits')}
                onChange={(vals) => setKpis(prev => prev.map(k => k.id === kpi.id ? { ...k, responsibleUnits: vals } : k))}
              />
            </View>
          </View>

          <KpiAnnualTargetSection
            config={kpi.target}
            onChange={(patch) => updateKPIField(kpi.id, 'target', { ...kpi.target, ...patch } as any)}
            unit={kpi.unit}
            frequency={kpi.frequency}
            startDate={kpi.startDate}
            formula={kpi.formula}
            dataSource={kpi.dataSource}
            onMeasurementChange={(field, value) => updateKPIField(kpi.id, field, value)}
          />
        </View>
      )}
    </View>
  );

  // Show loading while fetching activity data in edit mode
  if (initialLoading) {
    return (
      <PageContainer>
        <View style={styles.loadingFullContainer}>
          <ActivityIndicator size="large" color={tokens.colors.semantic.general["Main Color"]} />
          <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('navigation.initiatives'), route: 'Initiatives' },
            { label: isEditMode ? t('initiatives.editActivity') : t('initiatives.addActivity') },
          ]}
        />

        {/* Main Card Container */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }, isMobile && { padding: tokens.spacing.absolute["4"] }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('initiatives.editActivity') : t('initiatives.addActivity')}</Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: 30 }, isMobile && { padding: tokens.spacing.absolute["4"] }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('goals.basicInfo')}</Text>

            {/* Row 1: Activity Name (full width) */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.activityName')}</Text>
              </View>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.name && styles.inputError]}
                value={formData.name}
                onChangeText={(v) => updateField('name', v)}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
              {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
            </View>

            {/* Row 2: Description - Full width */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.activityDescription')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.description && styles.inputError]}
                value={formData.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder=""
                textAlign="right"
                multiline
                numberOfLines={3}
              />
              {errors.description && <Text style={styles.errorText}>{errors.description}</Text>}
            </View>

            {/* Row 3: Responsible Unit + Weight */}
            <View style={[styles.row, { zIndex: 20 }, isMobile && { flexDirection: 'column', gap: tokens.spacing.absolute["4"] }]}>
              <View style={[styles.fieldSmall, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.label}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <Button variant="secondary" size="sm" onPress={() => {}}>
                    {t('goals.setWeight')}
                  </Button>
                  <Text style={styles.percentSymbol}>%</Text>
                  <TextInput
                    style={styles.weightInput}
                    value={formData.weight}
                    onChangeText={(v) => updateField('weight', normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                    placeholder="30"
                    placeholderTextColor={tokens.colors.raw.gray["400"]}
                  />
                </View>
              </View>

              <View style={[styles.fieldLarge, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
                </View>
                <MultiSelectDropdown
                  values={formData.responsibleUnits}
                  options={orgUnitOptions}
                  placeholder={t('initiatives.selectResponsibleUnits')}
                  onChange={(vals) => {
                    setFormData(prev => ({ ...prev, responsibleUnits: vals }));
                    if (errors.responsibleUnits) {
                      setErrors(prev => ({ ...prev, responsibleUnits: '' }));
                    }
                  }}
                />
                {errors.responsibleUnits && <Text style={styles.errorText}>{errors.responsibleUnits}</Text>}
              </View>
            </View>
          </View>

          {/* Budget and Resources Section */}
          <View style={[styles.section, isMobile && { padding: tokens.spacing.absolute["4"] }]}>
            <Text style={styles.sectionTitle}>{t('initiatives.budgetAndResources')}</Text>

            {/* Row 1: Budget + Resources */}
            <View style={[styles.row, isMobile && { flexDirection: 'column', gap: tokens.spacing.absolute["4"] }]}>
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.label}>{t('initiatives.resources')}</Text>
                </View>
                <TextInput
                  style={styles.input}
                  value={formData.resources}
                  onChangeText={(v) => updateField('resources', v)}
                  placeholder=""
                  textAlign="right"
                />
              </View>

              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.label}>{t('initiatives.budget')}</Text>
                </View>
                <View style={styles.currencyInputContainer}>
                  <Text style={styles.currencyLabel}>{t('common.currency')}</Text>
                  <TextInput
                    style={styles.currencyInput}
                    value={formData.budget}
                    onChangeText={(v) => updateField('budget', v)}
                    keyboardType="numeric"
                    placeholder={t('initiatives.budgetPlaceholder')}
                    placeholderTextColor={tokens.colors.raw.gray["400"]}
                    textAlign="right"
                  />
                </View>
              </View>
            </View>

            {/* Row 2: Partners + Technical Resources */}
            <View style={[styles.row, isMobile && { flexDirection: 'column', gap: tokens.spacing.absolute["4"] }]}>
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.label}>{t('initiatives.technicalResources')}</Text>
                </View>
                <TextInput
                  style={styles.input}
                  value={formData.technicalResources}
                  onChangeText={(v) => updateField('technicalResources', v)}
                  placeholder={t('initiatives.addTechnicalResources')}
                  placeholderTextColor={tokens.colors.raw.gray["400"]}
                  textAlign="right"
                />
              </View>

              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.label}>{t('initiatives.partners')}</Text>
                </View>
                <TextInput
                  style={styles.input}
                  value={formData.partners}
                  onChangeText={(v) => updateField('partners', v)}
                  placeholder={t('initiatives.addPartners')}
                  placeholderTextColor={tokens.colors.raw.gray["400"]}
                  textAlign="right"
                />
              </View>
            </View>

            {/* Row 3: Start Date + End Date */}
            <View style={[styles.row, isMobile && { flexDirection: 'column', gap: tokens.spacing.absolute["4"] }]}>
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('initiatives.endDate')}</Text>
                </View>
                <View style={styles.dateInputContainer}>
                  {Platform.OS === 'web' ? (
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e: any) => updateField('endDate', e.target.value)}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        backgroundColor: 'transparent',
                        fontSize: tokens.typography.paragraph["paragraph small font size"],
                        fontFamily: 'inherit',
                        color: tokens.colors.semantic.general.foreground,
                        textAlign: getTextAlign(isRTL),
                        direction: 'rtl' as const,
                        padding: '10px 0',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <>
                      <CalendarIcon size={16} color={tokens.colors.raw.slate["500"]} />
                      <TextInput
                        style={[styles.dateInput, errors.endDate && styles.inputError]}
                        value={formData.endDate}
                        onChangeText={(v) => updateField('endDate', v)}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor={tokens.colors.raw.gray["400"]}
                        textAlign="right"
                      />
                    </>
                  )}
                </View>
                {errors.endDate && <Text style={styles.errorText}>{errors.endDate}</Text>}
              </View>

              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('initiatives.startDate')}</Text>
                </View>
                <View style={styles.dateInputContainer}>
                  {Platform.OS === 'web' ? (
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e: any) => updateField('startDate', e.target.value)}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        backgroundColor: 'transparent',
                        fontSize: tokens.typography.paragraph["paragraph small font size"],
                        fontFamily: 'inherit',
                        color: tokens.colors.semantic.general.foreground,
                        textAlign: getTextAlign(isRTL),
                        direction: 'rtl' as const,
                        padding: '10px 0',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <>
                      <CalendarIcon size={16} color={tokens.colors.raw.slate["500"]} />
                      <TextInput
                        style={[styles.dateInput, errors.startDate && styles.inputError]}
                        value={formData.startDate}
                        onChangeText={(v) => updateField('startDate', v)}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor={tokens.colors.raw.gray["400"]}
                        textAlign="right"
                      />
                    </>
                  )}
                </View>
                {errors.startDate && <Text style={styles.errorText}>{errors.startDate}</Text>}
              </View>
            </View>
          </View>

          {/* KPIs Section */}
          <View
            style={[styles.kpisSection, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}
            onLayout={(event) => {
              kpiSectionY.current = event.nativeEvent.layout.y;
            }}
          >
            <View style={styles.kpisSectionHeader}>
              <Text style={[styles.kpisSectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.performanceIndicators')}</Text>
            </View>

            {/* KPI Cards */}
            {kpis.map((kpi, index) => renderKPICard(kpi, index))}

            {/* Add KPI Button */}
            <Button
              variant="secondary"
              onPress={handleAddKPI}
              trailingIcon={<PlusIcon size={16} color={themeColors.iconColor} />}
            >
              {t('kpis.addKPI')}
            </Button>
          </View>

          {/* Action Buttons */}
          <View style={[styles.actionButtons, isMobile && { flexDirection: 'column', width: '100%' }]}>
            <Button
              variant="primary"
              onPress={handleSave}
              loading={loading}
              fullWidth={isMobile}
            >
              {t('common.save')}
            </Button>
            <Button
              variant="secondary"
              onPress={requestCancel}
              disabled={loading}
              fullWidth={isMobile}
            >
              {t('common.cancel')}
            </Button>
          </View>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmRemoveKPI}
        onCancel={() => setKpiPendingDelete(null)}
      />
      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.absolute["6"],
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  // Main Card
  mainCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },
  // Page Title
  pageTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },
  // Section
  section: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["6"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
    gap: tokens.spacing.absolute["6"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Field styles
  field: {
    gap: tokens.spacing.absolute["1_5"],
  },
  fieldSmall: {
    width: '30%',
  },
  fieldLarge: {
    flex: 1,
  },
  fieldHalf: {
    flex: 1,
    overflow: 'visible',
    gap: tokens.spacing.absolute["1_5"],
  },
  row: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["6"],
    overflow: 'visible',
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  requiredStar: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#D62728',
  },
  // Input styles
  input: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  textArea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  inputError: {
    borderColor: tokens.colors.raw.red["500"],
  },
  // Currency Input
  currencyInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    overflow: 'hidden',
  },
  currencyInput: {
    flex: 1,
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  currencyLabel: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    backgroundColor: tokens.colors.raw.slate["100"],
    borderStartWidth: tokens.spacing.absolute["px"],
    borderStartColor: tokens.colors.raw.slate["200"],
    paddingVertical: tokens.spacing.absolute["2_5"],
  },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  weightInput: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  percentSymbol: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  // Date Input
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2"],
  },
  dateInput: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Loading
  loadingContainer: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
  },
  loadingFullContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["4"],
  },
  loadingText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center',
  },
  // Dropdown styles
  dropdownContainer: {
    position: 'relative',
    zIndex: 1000,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dropdownDisabled: {
    backgroundColor: tokens.colors.raw.slate["100"],
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownPlaceholder: {
    color: tokens.colors.raw.slate["500"],
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    start: 0,
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-12"],
    marginTop: tokens.spacing.absolute["1"],
    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
    elevation: 20,
    zIndex: 9999,
    maxHeight: 200,
    paddingVertical: tokens.spacing.absolute["2"],
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // KPIs Section
  kpisSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  kpisSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["4"],
  },
  kpisSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // KPI Card
  kpiCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    marginBottom: tokens.spacing.absolute["3"],
    overflow: 'visible',
  },
  kpiCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    backgroundColor: tokens.colors.raw.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  kpiCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  kpiCardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  kpiHeaderButton: {
    width: 32,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.slate["100"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  kpiNumberBadge: {
    width: 28,
    height: 28,
    borderRadius: tokens.radii.absolute["radius-6"],
    backgroundColor: tokens.colors.raw.slate["100"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiNumberText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  kpiCardContent: {
    padding: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  kpiSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["3"],
    overflow: 'visible',
  },
  kpiSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
  },
  // Add KPI Button
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    direction: 'ltr' as any,
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["2"],
  },
  // Error styles
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["1"],
  },
});

const styles = getStyles(true);
